import { useEffect, useRef, useState } from 'react';
import type { ChatMessage, LrcLine, StationSnapshot } from '../lib/types';
import { api, type ControlAction } from '../lib/api';
import { ls } from '../lib/storage';
import { useBreath } from '../lib/useBreath';
import { useFavorites } from '../lib/useFavorites';
import { useParallax } from '../lib/useParallax';
import type { useAudio } from '../lib/useAudio';
import type { Coder } from '../lib/useCoder';
import { MountainScene } from '../components/MountainScene';
import { StageBackdrop } from '../components/StageBackdrop';
import { HalftoneWave } from '../components/HalftoneWave';
import { NowPlayingCard } from '../components/NowPlayingCard';
import { SubtitleStream, type CcMode } from '../components/SubtitleStream';
import { LyricsStrip } from '../components/LyricsStrip';
import { DayLog } from '../components/DayLog';
import { ProgramPanel } from '../components/ProgramPanel';
import { CommandLine } from '../components/CommandLine';
import { CodeTranscript } from '../components/CodeTranscript';
import { IconStop, IconPlus } from '../components/icons';

type AudioState = ReturnType<typeof useAudio>;

function ctl(action: ControlAction, value?: number) {
  api.control(action, value).catch(() => {});
}

const ccCycle: Record<CcMode, CcMode> = { card: 'persist', persist: 'off', off: 'card' };
const ccLabel: Record<CcMode, string> = { off: '关', card: '对卡', persist: '常驻' };

function CodeOverlay({ coder, onExit }: { coder: Coder; onExit(): void }) {
  const agentName = coder.engine === 'codex' ? 'Codex' : 'Claude Code';
  useEffect(() => {
    coder.ensureSubscribed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="glass-strong fixed inset-0 z-50 flex flex-col rounded-none border-0"
      style={{ background: 'color-mix(in srgb, var(--bg) 78%, transparent)' }}
    >
      <div className="flex items-center gap-3 px-5 pt-[max(2rem,env(safe-area-inset-top))] pb-2 text-xs tracking-[0.2em] text-muted">
        <button onClick={onExit} className="transition hover:text-fg">
          Code ▪ Esc 退出
        </button>
        {coder.working && (
          <button onClick={coder.stop} title="停止" className="text-red-400">
            <IconStop className="h-4 w-4" />
          </button>
        )}
        <button onClick={coder.reset} title="新会话">
          <IconPlus className="h-4 w-4" />
        </button>
      </div>
      <div className="min-h-0 flex-1 px-5 pb-24 flex flex-col">
        <CodeTranscript transcript={coder.transcript} working={coder.working} agentName={agentName} />
        {coder.note && <div className="pb-1 text-center text-[11px] text-red-400">{coder.note}</div>}
      </div>
      <CommandLine
        mode="code"
        busy={coder.working}
        onSend={coder.send}
        onEscape={onExit}
        zIndexClass="z-[60]"
        placeholder={`让 ${agentName} 写代码 / 修 bug…`}
      />
    </div>
  );
}

export function Player({
  snap,
  messages,
  historyLoaded,
  pushUser,
  working,
  statusLabel,
  connected,
  audio,
  offline,
  onOfflineNext,
  onOpenSettings,
  coder,
  mode,
  setMode,
}: {
  snap: StationSnapshot;
  messages: ChatMessage[];
  historyLoaded: boolean;
  pushUser: (t: string) => void;
  working: boolean;
  statusLabel: string;
  connected: boolean;
  audio: AudioState;
  offline: boolean;
  onOfflineNext: () => void;
  onOpenSettings: () => void;
  coder: Coder;
  mode: 'dj' | 'code';
  setMode: (m: 'dj' | 'code') => void;
}) {
  const nextCtl = () => {
    api.control('next').catch(() => onOfflineNext());
  };
  useParallax();
  const [logOpen, setLogOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [hint, setHint] = useState('');
  useEffect(() => {
    if (!hint) return;
    const t = setTimeout(() => setHint(''), 4000);
    return () => clearTimeout(t);
  }, [hint]);

  const [ccMode, setCcMode] = useState<CcMode>(() => {
    const v = ls.get('claudio.cc.mode');
    return v === 'off' || v === 'card' || v === 'persist' ? v : 'card';
  });
  const [cmdOpen, setCmdOpen] = useState(false);

  // 到了电脑前 = 不在开车：桌面端在“到场”时刻（页面打开/切回可见）自动退出行车模式；
  // 只在这两个时刻检测，桌面上手动开启不会被对抗。
  const drivingRef = useRef(snap.driving);
  drivingRef.current = snap.driving;
  const drvChecked = useRef(false);
  useEffect(() => {
    if (typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches) return;
    const clearIfDriving = () => {
      if (!drivingRef.current || document.hidden) return;
      api
        .setDriving(false)
        .then(() => setHint('已回到电脑前，行车模式自动关闭'))
        .catch(() => {});
    };
    const onVisible = () => {
      if (!document.hidden) clearIfDriving();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);
  useEffect(() => {
    if (drvChecked.current || snap.driving === undefined) return;
    drvChecked.current = true;
    if (typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches) return;
    if (snap.driving && !document.hidden) {
      api
        .setDriving(false)
        .then(() => setHint('已回到电脑前，行车模式自动关闭'))
        .catch(() => {});
    }
  }, [snap.driving]);
  const np = snap.nowPlaying?.track;
  const waitingForModeTrack = !!snap.modePreparing && snap.status !== 'playing';
  const fav = useFavorites();
  const playing = snap.status === 'playing' && !audio.blocked;
  const breath = useBreath(audio.el, playing, np?.videoId ?? null);

  const [synced, setSynced] = useState<LrcLine[]>([]);
  const [lyricsImmersive, setLyricsImmersive] = useState(false);
  const hasLyricsRef = useRef(false);
  hasLyricsRef.current = synced.length > 0;
  const lyricsKey = np ? np.videoId ?? `${np.title}|${np.artist}` : null;

  useEffect(() => {
    setLyricsImmersive(false);
    if (!np) {
      setSynced([]);
      return;
    }
    let cancelled = false;
    setSynced([]);
    api
      .lyrics(np.title, np.artist, np.durationSec)
      .then((r) => !cancelled && setSynced(r.synced ?? []))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lyricsKey]);

  // ⌘L / Ctrl+L never opens the type-to-talk box (CommandLine bails on any
  // metaKey/ctrlKey keydown), so this listener can't hijack keystrokes meant for it.
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (mode !== 'dj') return;
      if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey) return;
      if (e.key.toLowerCase() !== 'l') return;
      if (e.isComposing || e.keyCode === 229) return;
      const el = document.activeElement as HTMLElement | null;
      const tag = el?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable) return;
      e.preventDefault();
      if (hasLyricsRef.current) setLyricsImmersive((v) => !v);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [mode]);

  const gesture = () => {
    audio.resume();
    breath.unlock();
  };
  const toggle = () => {
    if (playing && audio.speaker) {
      // listening → pause the station and switch this radio off
      audio.setSpeaker(false);
      audio.el.pause();
      ctl('pause');
    } else if (playing) {
      // station already playing elsewhere → just join with this device
      audio.setSpeaker(true);
      gesture();
    } else {
      audio.setSpeaker(true);
      gesture();
      ctl('play');
    }
  };
  const jump = (uid: string) => {
    audio.setSpeaker(true); // picking a song on this device means "listen here" — no second tap
    gesture();
    api.jump(uid).catch(() => {});
  };
  const onSeek = (t: number) => {
    audio.seek(t);
    api.control('seek', t).catch(() => {});
  };
  const volDelta = (d: number) => audio.setVolume(Math.max(0, Math.min(1, audio.volume + d)));
  const sendDj = async (t: string) => {
    pushUser(t);
    try {
      await api.chat(t);
    } catch {
      /* ignore */
    }
  };

  const cmd = (c: 'code' | 'settings' | 'cc' | 'driving-on' | 'driving-off' | 'reading-on' | 'reading-off') => {
    if (c === 'settings') return onOpenSettings();
    if (c === 'cc') {
      setCcMode((m) => {
        const next = ccCycle[m];
        ls.set('claudio.cc.mode', next);
        setHint(`cc：${ccLabel[next]}`);
        return next;
      });
      return;
    }
    if (c === 'driving-on' || c === 'driving-off') {
      const on = c === 'driving-on';
      api
        .setDriving(on)
        .then(() => setHint(`行车模式已${on ? '开启' : '关闭'}`))
        .catch(() => setHint('行车模式设置失败'));
      return;
    }
    if (c === 'reading-on' || c === 'reading-off') {
      const on = c === 'reading-on';
      api
        .setReading(on)
        .then(() => setHint(on ? '阅读模式已开启，好好读' : '阅读模式已关闭'))
        .catch(() => setHint('阅读模式设置失败'));
      return;
    }
    if (!coder.available) return setHint('Code 模式在这台设备上不可用');
    if (ls.get('claudio.codeMode.ack') === '1') setMode('code');
    else setConfirm(true);
  };
  const confirmCode = () => {
    ls.set('claudio.codeMode.ack', '1');
    setConfirm(false);
    setMode('code');
  };

  return (
    <div className="relative h-full overflow-clip">
      <MountainScene driving={!!snap.driving} reading={!!snap.reading} />
      <StageBackdrop
        driving={snap.driving}
        reading={snap.reading}
        listeners={snap.listeners}
        onToggleDriving={() => {
          const on = !snap.driving;
          api
            .setDriving(on)
            .then(() => setHint(`行车模式已${on ? '开启' : '关闭'}，下一首准备中…`))
            .catch(() => setHint('行车模式设置失败'));
        }}
        onToggleReading={() => {
          const on = !snap.reading;
          api
            .setReading(on)
            .then(() => setHint(`${on ? '阅读模式已开启' : '阅读模式已关闭'}，下一首准备中…`))
            .catch(() => setHint('阅读模式设置失败'));
        }}
      />

      <div className="par-mid pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-5 px-4 pb-[24dvh] md:items-start md:pl-[8vw]">
        <div className="pointer-events-auto">
          <NowPlayingCard
            title={waitingForModeTrack ? '准备中' : np?.title ?? ''}
            artist={
              waitingForModeTrack
                ? `正在准备${snap.reading ? '阅读' : snap.driving ? '行车' : '日常'}模式的下一首`
                : np?.artist ?? ''
            }
            videoId={waitingForModeTrack ? undefined : np?.videoId}
            playing={playing}
            blocked={audio.blocked}
            muted={playing && !audio.speaker}
            connected={connected}
            offline={offline}
            fetching={snap.status === 'playing' && !!np && !np.file}
            buffering={!!np?.file && playing && audio.speaker && audio.buffering}
            picking={working && !np}
            modePreparing={!!snap.modePreparing}
            volume={audio.volume}
            currentTime={audio.currentTime}
            duration={audio.duration}
            onToggle={toggle}
            onPrev={() => {
              gesture();
              ctl('prev');
            }}
            onNext={() => {
              gesture();
              nextCtl();
            }}
            onVolume={audio.setVolume}
            onSeek={onSeek}
            liked={fav.isLiked(np?.title, np?.artist)}
            onStamp={() => {
              if (np?.title) fav.toggle(np.title, np.artist ?? '');
            }}
            waveform={<HalftoneWave breath={breath} playing={playing} working={working} />}
            lyrics={
              <LyricsStrip
                synced={synced}
                currentTime={audio.currentTime}
                fallback={np?.say || np?.reason || ''}
                onSeek={onSeek}
                immersive={lyricsImmersive}
                onImmersiveChange={setLyricsImmersive}
              />
            }
          />
        </div>
      </div>

      <ProgramPanel open={logOpen} onOpenChange={setLogOpen} hotkeysDisabled={mode === 'code'}>
        <DayLog snap={snap} onJump={jump} onOpenSettings={onOpenSettings} />
      </ProgramPanel>

      {mode === 'dj' && (
        <SubtitleStream
          messages={messages}
          historyLoaded={historyLoaded}
          onForget={(n) => api.forgetTaste(n).catch(() => {})}
          ccMode={ccMode}
          historyOpen={cmdOpen}
        />
      )}

      {mode === 'dj' && (
        <CommandLine
          mode="dj"
          busy={false}
          onSend={sendDj}
          onCommand={cmd}
          onOpenChange={setCmdOpen}
          onEscape={() => setLyricsImmersive(false)}
          onTogglePlay={toggle}
          onPrev={() => {
            gesture();
            ctl('prev');
          }}
          onNext={() => {
            gesture();
            nextCtl();
          }}
          onVolumeDelta={volDelta}
        />
      )}
      {mode === 'code' && <CodeOverlay coder={coder} onExit={() => setMode('dj')} />}

      {hint && (
        <div className="pointer-events-none fixed inset-x-0 bottom-[calc(3rem+max(1rem,env(safe-area-inset-bottom)))] z-40 px-6 text-center text-xs text-muted">
          {hint}
        </div>
      )}

      {confirm && (
        <div className="fixed inset-0 z-[70] grid place-items-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="glass glass-strong max-w-sm space-y-3 rounded-[20px] p-4 text-sm">
            <div className="font-display text-base text-fg">进入 Code 模式</div>
            <p className="text-muted">
              Code 模式直接运行本机的 {coder.engine === 'codex' ? 'Codex' : 'Claude Code'}，
              拥有<strong className="text-fg">完整权限</strong>
              ：可以读写、运行任意命令、修改 claudio 自己的代码。请仅在你信任的机器上使用。
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirm(false)}
                className="rounded-lg border bd px-3 py-1.5 text-muted transition hover:text-fg"
              >
                取消
              </button>
              <button
                onClick={confirmCode}
                className="accent-grad text-on-accent rounded-lg px-3 py-1.5 font-semibold"
              >
                我了解，继续
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
